import React, { useState, useEffect } from 'react';
import BotonManualHeader from "../../components/BotonManualHeader";
import {
    Container, Row, Col, Card, Form, Button,
    ListGroup, Badge, InputGroup, FormControl, Alert, OverlayTrigger, Tooltip, Spinner
} from 'react-bootstrap';
import firebaseApp from '../../firebase/credenciales';
import { collection, getDocs, doc, setDoc, updateDoc, deleteDoc, serverTimestamp, getFirestore, query, where, orderBy } from 'firebase/firestore';
import { BiUserPlus, BiGroup, BiSave, BiX, BiFilterAlt, BiCheckSquare, BiUserX } from 'react-icons/bi';
import { FaSearch, FaUsers, FaSyncAlt, FaLayerGroup, FaMapMarkerAlt, FaFilePdf, FaTrash } from 'react-icons/fa';
import Swal from 'sweetalert2';
import { logBusinessAction } from '../../services/AuditService';
import { syncGroupState } from '../../services/groupAssignmentService';

const db = getFirestore(firebaseApp);

const GruposEditor = ({ usuario }) => {
    const sincronizarMembresiasYCoordenadas = async ({
        groupId,
        groupName,
        currentMemberDnis,
        currentSedeIds,
        previousMemberDnis,
        previousSedeIds,
    }) => {
        const companyName = users[0]?.empresa || "Mi Empresa";
        await syncGroupState({
            rucEmpresa,
            groupId,
            groupName,
            currentMemberDnis,
            currentSedeIds,
            previousMemberDnis,
            previousSedeIds,
            sedesData: sedes,
            companyName,
        });
    };

    // Estados
    const [users, setUsers] = useState([]);
    const [rucEmpresa, setRucEmpresa] = useState(usuario.ruc);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [groups, setGroups] = useState([]);
    const [currentGroup, setCurrentGroup] = useState({
        id: '',
        name: '',
        description: '',
        members: [],
        sedes: [] // Array de IDs de sedes asignadas
    });
    const [sedes, setSedes] = useState([]); // Todas las sedes disponibles
    const [showSedesSelector, setShowSedesSelector] = useState(false); // Estado para controlar visibilidad del selector
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mode, setMode] = useState('new');

    // Nuevo estado para el filtro de cargo
    const [cargos, setCargos] = useState([]);
    const [cargoFilter, setCargoFilter] = useState('');

    // Nuevo estado para usuarios sin grupo
    const [usersWithoutGroup, setUsersWithoutGroup] = useState([]);
    const [showUsersWithoutGroup, setShowUsersWithoutGroup] = useState(false);

    // Estado para datos iniciales
    const [datosIniciales, setDatosIniciales] = useState(false);

    // Cargar usuarios y grupos al inicializar
    useEffect(() => {
        if (!rucEmpresa) {
            setError("Error: No se pudo determinar la empresa del usuario");
            setLoading(false);
            return;
        }

        const fetchData = async () => {
            try {
                setLoading(true);
                await Promise.all([fetchUsers(), fetchGroups(), fetchSedes()]);
                setLoading(false);
                setDatosIniciales(true);
            } catch (err) {
                console.error("Error al cargar datos:", err);
                setError("Error al cargar los datos. Por favor, intente nuevamente.");
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Calcular usuarios sin grupo cuando cambien usuarios o grupos
    useEffect(() => {
        if (users.length > 0 && groups.length >= 0) {
            const allGroupMembers = new Set();

            // Recopilar todos los DNIs de usuarios que están en algún grupo
            groups.forEach(group => {
                if (group.members) {
                    group.members.forEach(dni => allGroupMembers.add(dni));
                }
            });

            // Filtrar usuarios que NO están en ningún grupo
            const withoutGroup = users.filter(user => !allGroupMembers.has(user.dni));
            setUsersWithoutGroup(withoutGroup);
        }
    }, [users, groups]);

    // Extraer cargos únicos cuando se cargan usuarios
    useEffect(() => {
        if (users.length > 0) {
            const uniqueCargos = [...new Set(users.map(user => user.cargo).filter(Boolean))];
            uniqueCargos.sort();
            setCargos(uniqueCargos);
        }
    }, [users]);

    // Filtrar usuarios cuando cambie el término de búsqueda o el filtro de cargo
    useEffect(() => {
        let filtered = [...users];

        // Aplicar filtro de texto
        if (searchTerm.trim() !== '') {
            const lowercasedSearch = searchTerm.toLowerCase();
            filtered = filtered.filter(
                user =>
                    user.name?.toLowerCase().includes(lowercasedSearch) ||
                    user.apellidos?.toLowerCase().includes(lowercasedSearch) ||
                    user.dni?.includes(searchTerm) ||
                    user.email?.toLowerCase().includes(lowercasedSearch) ||
                    user.cargo?.toLowerCase().includes(lowercasedSearch)
            );
        }

        // Aplicar filtro de cargo
        if (cargoFilter) {
            filtered = filtered.filter(user => user.cargo === cargoFilter);
        }

        setFilteredUsers(filtered);
    }, [searchTerm, cargoFilter, users]);

    // Cargar todos los usuarios
    const fetchUsers = async () => {
        try {
            const usersCollectionRef = collection(db, 'users');
            const usersQuery = query(usersCollectionRef, where("rucEmpresa", "==", rucEmpresa));
            const usersSnapshot = await getDocs(usersQuery);

            const usersData = [];
            usersSnapshot.forEach((doc) => {
                const userData = doc.data();
                // Filtrar por usuarios activos Y que pertenezcan a la misma empresa (rucEmpresa)
                if (userData.activo !== false && userData.rucEmpresa === rucEmpresa) {
                    usersData.push({
                        ...userData,
                        id: doc.id
                    });
                }
            });

            setUsers(usersData);
            setFilteredUsers(usersData);
            return usersData;
        } catch (error) {
            console.error("Error al obtener usuarios:", error);
            setError("Error al cargar usuarios de la base de datos");
            throw error;
        }
    };

    // Nueva función para seleccionar/deseleccionar todos los usuarios filtrados
    const toggleSelectAll = () => {
        // Verificar si todos los usuarios filtrados ya están seleccionados
        const allSelected = filteredUsers.every(user =>
            selectedUsers.some(selectedUser => selectedUser.dni === user.dni)
        );

        if (allSelected) {
            // Si todos están seleccionados, quitamos los usuarios filtrados de la selección
            const newSelectedUsers = selectedUsers.filter(user =>
                !filteredUsers.some(filteredUser => filteredUser.dni === user.dni)
            );
            setSelectedUsers(newSelectedUsers);
        } else {
            // Si no todos están seleccionados, agregamos los usuarios filtrados que no estén ya seleccionados
            const usersToAdd = filteredUsers.filter(user =>
                !selectedUsers.some(selectedUser => selectedUser.dni === user.dni)
            );
            setSelectedUsers([...selectedUsers, ...usersToAdd]);
        }
    };

    // Verificar si todos los usuarios filtrados están seleccionados
    const areAllSelected = filteredUsers.length > 0 && filteredUsers.every(user =>
        selectedUsers.some(selectedUser => selectedUser.dni === user.dni)
    );

    // Restablecer filtros
    const resetFilters = () => {
        setSearchTerm('');
        setCargoFilter('');
    };

    // Cargar grupos existentes
    const fetchGroups = async () => {
        try {
            // Usar la subcollección específica para el RUC de la empresa
            const groupsCollectionRef = collection(db, `grupos/${rucEmpresa}/grupos`);
            const groupsSnapshot = await getDocs(groupsCollectionRef);

            const groupsData = [];
            groupsSnapshot.forEach((doc) => {
                groupsData.push({
                    ...doc.data(),
                    id: doc.id
                });
            });

            setGroups(groupsData);
            return groupsData;
        } catch (error) {
            console.error("Error al obtener grupos:", error);
            setError("Error al cargar grupos de la base de datos");
            throw error;
        }
    };

    // Cargar sedes de la empresa
    const fetchSedes = async () => {
        try {
            if (!rucEmpresa) return;
            const sedesCollectionRef = collection(db, `company_cordenadas/${rucEmpresa}/sedes`);
            const q = query(sedesCollectionRef, orderBy('nombre'));
            const sedesSnapshot = await getDocs(q);

            const sedesData = [];
            sedesSnapshot.forEach((doc) => {
                const data = doc.data();
                // Solo cargar sedes activas, o todas? Mejor todas para visualizarlas.
                sedesData.push({
                    id: doc.id,
                    ...data
                });
            });
            setSedes(sedesData);
            return sedesData;
        } catch (error) {
            console.error("Error al obtener sedes:", error);
            // No bloqueamos la UI si fallan las sedes, solo logueamos
        }
    };

    // Seleccionar/deseleccionar un usuario
    const toggleUserSelection = (user) => {
        if (selectedUsers.some(u => u.dni === user.dni)) {
            setSelectedUsers(selectedUsers.filter(u => u.dni !== user.dni));
        } else {
            setSelectedUsers([...selectedUsers, user]);
        }
    };

    // Seleccionar un grupo para editar
    const selectGroup = (group) => {
        setCurrentGroup({
            id: group.id,
            name: group.name,
            description: group.description || '',
            members: group.members || [],
            sedes: group.sedes || [] // Cargar sedes guardadas
        });

        // Mostrar selector solo si tiene múltiples sedes asignadas
        const hasMultipleSedes = (group.sedes && group.sedes.length > 1);
        setShowSedesSelector(hasMultipleSedes);

        // Seleccionar los usuarios que ya están en el grupo
        const groupMembers = users.filter(user =>
            group.members && group.members.includes(user.dni)
        );
        setSelectedUsers(groupMembers);
        setMode('edit');
    };

    // Crear nuevo grupo
    const createNewGroup = () => {
        setCurrentGroup({ id: '', name: '', description: '', members: [], sedes: [] });
        setSelectedUsers([]);
        setMode('new');
        setShowSedesSelector(false);
    };

    // Guardar grupo (crear o actualizar)
    const saveGroup = async () => {
        try {
            if (!currentGroup.name.trim()) {
                Swal.fire('Atención', 'El nombre del grupo es obligatorio', 'warning');
                return;
            }

            if (selectedUsers.length === 0) {
                Swal.fire('Atención', 'Debe seleccionar al menos un usuario para el grupo', 'warning');
                return;
            }

            setLoading(true);

            // Lógica de Asignación Automática de Sedes si no hay selección explícita
            let finalSedesIds = currentGroup.sedes || [];
            if (finalSedesIds.length === 0) {
                // Intentar buscar sede por nombre exacto o normalizado
                const normalizedGroupName = currentGroup.name.trim().toLowerCase();
                const matchingSede = sedes.find(s =>
                    s.nombre.toLowerCase() === normalizedGroupName ||
                    (s.regimenNombre && s.regimenNombre.toLowerCase() === normalizedGroupName)
                );

                if (matchingSede) {
                    finalSedesIds = [matchingSede.id];
                }
            }

            const memberDnis = selectedUsers.map(user => user.dni);
            const groupData = {
                name: currentGroup.name.trim(),
                description: currentGroup.description.trim(),
                members: memberDnis,
                sedes: finalSedesIds, // Usar la lista final (seleccionada o auto-asignada)
                rucEmpresa: rucEmpresa,
                updatedAt: serverTimestamp()
            };

            let groupId;
            const prevGroup = mode === 'edit'
                ? groups.find((g) => String(g.id) === String(currentGroup.id))
                : null;
            const previousMemberDnis = prevGroup?.members || [];
            const previousSedeIds = prevGroup?.sedes || [];
            if (mode === 'new') {
                // Crear grupo con ID autogenerado en la subcollección de la empresa
                const groupsCollection = collection(db, `grupos/${rucEmpresa}/grupos`);
                const newGroupRef = doc(groupsCollection);
                groupId = newGroupRef.id;
                groupData.createdAt = serverTimestamp();

                // Asegurar que el documento principal existe (si es necesario)
                const rucDocRef = doc(db, 'grupos', rucEmpresa);
                await setDoc(rucDocRef, {
                    ruc: rucEmpresa,
                    lastUpdated: serverTimestamp()
                }, { merge: true });

                // Ahora guardar el grupo en la subcollección
                await setDoc(newGroupRef, groupData);

                await sincronizarMembresiasYCoordenadas({
                    groupId,
                    groupName: currentGroup.name.trim(),
                    currentMemberDnis: memberDnis,
                    currentSedeIds: finalSedesIds,
                    previousMemberDnis: [],
                    previousSedeIds: [],
                });

                await logBusinessAction("CREACION_GRUPO", "GestionUsuarios", { grupoId: groupId, grupoNombre: currentGroup.name.trim() }, usuario);

                Swal.fire({
                    icon: 'success',
                    title: 'Grupo creado',
                    text: `El grupo "${currentGroup.name}" se ha creado correctamente.${finalSedesIds.length > 0 ? ' Se asignaron sedes automáticamente.' : ''}`
                });
            } else {
                // Actualizar grupo existente en la subcollección
                groupId = currentGroup.id;
                const groupRef = doc(db, `grupos/${rucEmpresa}/grupos`, groupId);
                await updateDoc(groupRef, groupData);

                await sincronizarMembresiasYCoordenadas({
                    groupId: String(groupId),
                    groupName: currentGroup.name.trim(),
                    currentMemberDnis: memberDnis,
                    currentSedeIds: finalSedesIds,
                    previousMemberDnis,
                    previousSedeIds,
                });

                await logBusinessAction("EDICION_GRUPO", "GestionUsuarios", { grupoId: groupId, grupoNombre: currentGroup.name.trim() }, usuario);

                Swal.fire({
                    icon: 'success',
                    title: 'Grupo actualizado',
                    text: `El grupo "${currentGroup.name}" se ha actualizado correctamente`
                });
            }

            // Actualizar la UI
            await fetchGroups();
            createNewGroup(); // Limpiar el formulario
            setError(null);
        } catch (err) {
            console.error("Error al guardar el grupo:", err);
            setError(`Error al ${mode === 'new' ? 'crear' : 'actualizar'} el grupo. Por favor, intente nuevamente.`);
        } finally {
            setLoading(false);
        }
    };

    // Eliminar un usuario seleccionado
    const removeSelectedUser = (user) => {
        setSelectedUsers(selectedUsers.filter(u => u.dni !== user.dni));
    };

    // Función para eliminar grupo con doble confirmación
    const handleDeleteGroup = async (group) => {
        // Primera confirmación (Warning)
        const result1 = await Swal.fire({
            title: '¿Eliminar grupo?',
            text: `Estás a punto de eliminar el grupo "${group.name}". Esta acción no se puede deshacer.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            confirmButtonText: 'Sí, continuar',
            cancelButtonText: 'Cancelar'
        });

        if (!result1.isConfirmed) return;

        // Segunda confirmación (Específica)
        const result2 = await Swal.fire({
            title: 'Confirmación final',
            text: `Por favor, escribe el nombre del grupo "${group.name}" para confirmar la eliminación:`,
            input: 'text',
            inputPlaceholder: group.name,
            showCancelButton: true,
            confirmButtonText: 'Eliminar definitivamente',
            cancelButtonText: 'Cancelar',
            confirmButtonColor: '#d33',
            preConfirm: (inputValue) => {
                if (inputValue !== group.name) {
                    Swal.showValidationMessage('El nombre ingresado no coincide');
                }
            }
        });

        if (!result2.isConfirmed) return;

        // Ejecutar eliminación
        try {
            console.log("Iniciando eliminación del grupo:", group.id);
            setLoading(true);
            await sincronizarMembresiasYCoordenadas({
                groupId: String(group.id),
                groupName: String(group.name || ""),
                currentMemberDnis: [],
                currentSedeIds: [],
                previousMemberDnis: group.members || [],
                previousSedeIds: group.sedes || [],
            });
            const groupRef = doc(db, `grupos/${rucEmpresa}/grupos`, group.id);
            await deleteDoc(groupRef);
            await logBusinessAction("ELIMINACION_GRUPO", "GestionUsuarios", { grupoId: group.id, grupoNombre: group.name }, usuario);
            console.log("Grupo eliminado de Firestore");

            // Optimización: Actualizar estado localmente en lugar de recargar todo
            setGroups(prevGroups => prevGroups.filter(g => g.id !== group.id));

            // Si el grupo eliminado es el que se está editando, limpiar el formulario
            if (currentGroup.id === group.id) {
                createNewGroup();
            }

            setLoading(false); // Detener carga antes de mostrar alerta

            await Swal.fire(
                '¡Eliminado!',
                `El grupo "${group.name}" ha sido eliminado correctamente.`,
                'success'
            );

        } catch (error) {
            console.error("Error al eliminar grupo:", error);
            setLoading(false);
            Swal.fire(
                'Error',
                'Hubo un problema al eliminar el grupo.',
                'error'
            );
        }
    };

    // Función para limpiar búsqueda
    const limpiarBusqueda = () => {
        setSearchTerm('');
        resetFilters();
    };

    return (
        <div
            className="min-vh-100"
            style={{
                background: "linear-gradient(135deg, #ffffff 0%, #ffffff 100%)",
                minHeight: "100vh",
                width: "100vw",
                maxWidth: "100%",
                padding: "20px 0",
            }}
        >
            <Container fluid style={{ width: "90%", maxWidth: "100%" }}>
                {/* Marco principal */}
                <Card
                    className="shadow-lg border-0"
                    style={{
                        borderRadius: "15px",
                        overflow: "hidden",
                        minHeight: "90vh",
                    }}
                >
                    {/* Header Premium con degradado azul */}
                    <Card.Header
                        className="text-white py-4 position-relative"
                        style={{
                            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                            borderBottom: "3px solid #f59e0b",
                        }}
                    >
                        <div className="position-absolute end-0 top-50 translate-middle-y me-4 d-flex align-items-center gap-2" style={{ zIndex: 10 }}>
                            <BotonManualHeader />
                            <Button
                                variant="warning"
                                size="sm"
                                href="https://firebasestorage.googleapis.com/v0/b/tuboletaya.appspot.com/o/Manuales%2F20609212102%2FMANUAL%20GESTI%C3%93N%20DE%20GRUPOS%20DE%20USUARIOS.pdf?alt=media&token=ef2a4787-474d-4e30-a5b9-2c5739964524"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="d-flex align-items-center gap-2 fw-bold"
                                style={{ borderRadius: "8px", fontSize: "13px", padding: "6px 12px", border: "none" }}
                            >
                                <FaFilePdf /> Descargar PDF
                            </Button>
                        </div>
                        <div className="text-center">
                            <h2 className="fw-bold mb-1">
                                <FaLayerGroup className="me-3" />
                                Editor de Grupos
                            </h2>
                            <p className="mb-0 opacity-75">
                                Administra y organiza los grupos de usuarios del sistema
                            </p>
                        </div>
                    </Card.Header>

                    <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
                        {/* Indicador de carga inicial */}
                        {!datosIniciales && users.length === 0 && !loading && (
                            <div className="text-center py-5">
                                <Spinner animation="border" variant="primary" />
                                <p className="text-muted mt-3 mb-0">Cargando datos...</p>
                            </div>
                        )}

                        {/* Indicador de carga para operaciones */}
                        {loading && (
                            <div
                                className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-75"
                                style={{ zIndex: 1080 }}
                            >
                                <div className="text-center">
                                    <Spinner
                                        animation="border"
                                        variant="light"
                                        style={{ width: "3rem", height: "3rem" }}
                                    />
                                    <p className="text-white mt-3 mb-0">
                                        Procesando solicitud...
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Contenido principal */}
                        {datosIniciales && (
                            <>
                                {/* Alerta de error */}
                                {error && (
                                    <Alert variant="danger" onClose={() => setError(null)} dismissible className="mb-4">
                                        {error}
                                    </Alert>
                                )}

                                {/* Barra superior con información */}
                                <Card
                                    className="shadow-sm border-0 mb-4"
                                    style={{ borderRadius: "15px" }}
                                >
                                    <Card.Body className="p-4">
                                        <Row className="align-items-center">
                                            <Col md={1}>
                                                <div
                                                    className="rounded-3 p-3 text-center"
                                                    style={{
                                                        background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                        color: "white",
                                                    }}
                                                >
                                                    <FaLayerGroup size={24} />
                                                </div>
                                            </Col>
                                            <Col md={7}>
                                                <h5 className="mb-1 fw-semibold">
                                                    Gestión de Grupos
                                                </h5>
                                                <p className="text-muted mb-0 small">
                                                    Organiza usuarios en grupos para facilitar la administración
                                                </p>
                                            </Col>

                                        </Row>
                                    </Card.Body>
                                </Card>

                                {/* Estadísticas */}
                                <Row className="mb-4">
                                    <Col md={3}>
                                        <Card
                                            className="border-0 shadow-sm"
                                            style={{ borderRadius: "15px" }}
                                        >
                                            <Card.Body
                                                className="text-center"
                                                style={{
                                                    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                    color: "white",
                                                    borderRadius: "15px",
                                                }}
                                            >
                                                <FaLayerGroup size="2rem" className="mb-2" />
                                                <h4 className="fw-bold">{groups.length}</h4>
                                                <p className="mb-0">Grupos Totales</p>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card
                                            className="border-0 shadow-sm"
                                            style={{ borderRadius: "15px" }}
                                        >
                                            <Card.Body
                                                className="text-center"
                                                style={{
                                                    background: "linear-gradient(135deg, #059669 0%, #10b981 100%)",
                                                    color: "white",
                                                    borderRadius: "15px",
                                                }}
                                            >
                                                <FaUsers size="2rem" className="mb-2" />
                                                <h4 className="fw-bold">{users.length}</h4>
                                                <p className="mb-0">Usuarios Totales</p>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card
                                            className="border-0 shadow-sm"
                                            style={{ borderRadius: "15px" }}
                                        >
                                            <Card.Body
                                                className="text-center"
                                                style={{
                                                    background: "linear-gradient(135deg, #dc2626 0%, #ef4444 100%)",
                                                    color: "white",
                                                    borderRadius: "15px",
                                                }}
                                            >
                                                <BiUserX size="2rem" className="mb-2" />
                                                <h4 className="fw-bold">{usersWithoutGroup.length}</h4>
                                                <p className="mb-0">Sin Grupo</p>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                    <Col md={3}>
                                        <Card
                                            className="border-0 shadow-sm"
                                            style={{ borderRadius: "15px" }}
                                        >
                                            <Card.Body
                                                className="text-center"
                                                style={{
                                                    background: "linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)",
                                                    color: "white",
                                                    borderRadius: "15px",
                                                }}
                                            >
                                                <BiCheckSquare size="2rem" className="mb-2" />
                                                <h4 className="fw-bold">{users.length - usersWithoutGroup.length}</h4>
                                                <p className="mb-0">En Grupos</p>
                                            </Card.Body>
                                        </Card>
                                    </Col>
                                </Row>

                                {/* Barra de búsqueda */}
                                <Card
                                    className="shadow-sm border-0 mb-4"
                                    style={{ borderRadius: "15px" }}
                                >
                                    <Card.Body className="p-3">
                                        <Form onSubmit={(e) => e.preventDefault()}>
                                            <Row className="g-2 align-items-center">
                                                <Col md={5}>
                                                    <div className="position-relative">
                                                        <FaSearch
                                                            className="position-absolute"
                                                            style={{
                                                                left: "12px",
                                                                top: "50%",
                                                                transform: "translateY(-50%)",
                                                                color: "#6c757d",
                                                                zIndex: 2,
                                                            }}
                                                        />
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Buscar usuarios por nombre, apellido, DNI, cargo o email..."
                                                            className="form-control-lg ps-5"
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            style={{
                                                                borderRadius: "10px",
                                                                border: "2px solid #e9ecef",
                                                            }}
                                                        />
                                                    </div>
                                                </Col>
                                                <Col md={3}>
                                                    <Form.Select
                                                        value={cargoFilter}
                                                        onChange={(e) => setCargoFilter(e.target.value)}
                                                        className="form-select-lg"
                                                        style={{
                                                            borderRadius: "10px",
                                                            border: "2px solid #e9ecef",
                                                        }}
                                                    >
                                                        <option value="">Todos los cargos</option>
                                                        {cargos.map(cargo => (
                                                            <option key={cargo} value={cargo}>
                                                                {cargo}
                                                            </option>
                                                        ))}
                                                    </Form.Select>
                                                </Col>
                                                <Col md={2}>
                                                    <Button
                                                        variant="outline-secondary"
                                                        size="lg"
                                                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                        onClick={resetFilters}
                                                        style={{ borderRadius: "10px" }}
                                                    >
                                                        <BiFilterAlt size="16px" />
                                                        Limpiar
                                                    </Button>
                                                </Col>
                                                <Col md={2}>
                                                    <Button
                                                        variant="primary"
                                                        size="lg"
                                                        className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                        onClick={limpiarBusqueda}
                                                        style={{
                                                            borderRadius: "10px",
                                                            background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                            border: "none",
                                                        }}
                                                    >
                                                        <FaSyncAlt size="16px" />
                                                        Refrescar
                                                    </Button>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </Card.Body>
                                </Card>

                                <Row>
                                    {/* Panel izquierdo: Lista de usuarios */}
                                    <Col lg={5}>
                                        <Card className="mb-4 border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                            <Card.Header
                                                className="text-white py-3"
                                                style={{
                                                    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                    borderRadius: "15px 15px 0 0",
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0 fw-semibold">Usuarios Disponibles</h5>
                                                    <div className="d-flex align-items-center gap-2">
                                                        <Badge bg="light" text="dark" className="me-2">
                                                            {(showUsersWithoutGroup ? usersWithoutGroup : filteredUsers).length} usuarios
                                                            {cargoFilter && <span> ({cargoFilter})</span>}
                                                        </Badge>
                                                        <Button
                                                            variant={showUsersWithoutGroup ? "warning" : "light"}
                                                            size="sm"
                                                            onClick={() => setShowUsersWithoutGroup(!showUsersWithoutGroup)}
                                                            className="d-flex align-items-center me-2"
                                                            title={showUsersWithoutGroup ? "Ver todos los usuarios" : "Ver solo usuarios sin grupo"}
                                                            style={{ borderRadius: "8px" }}
                                                        >
                                                            <BiUserX size="14px" className="me-1" />
                                                            {showUsersWithoutGroup ? "Todos" : "Sin Grupo"}
                                                        </Button>
                                                        {(showUsersWithoutGroup ? usersWithoutGroup : filteredUsers).length > 0 && !showUsersWithoutGroup && (
                                                            <Button
                                                                variant={areAllSelected ? "warning" : "light"}
                                                                size="sm"
                                                                onClick={toggleSelectAll}
                                                                className="d-flex align-items-center"
                                                                title={areAllSelected ? "Deseleccionar todos" : "Seleccionar todos"}
                                                                style={{ borderRadius: "8px" }}
                                                            >
                                                                <BiCheckSquare size="14px" className="me-1" />
                                                                {areAllSelected ? "Deseleccionar" : "Seleccionar"}
                                                            </Button>
                                                        )}
                                                    </div>
                                                </div>
                                            </Card.Header>
                                            <Card.Body className="p-0">
                                                <div className="user-list" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                                                    <ListGroup variant="flush">
                                                        {(showUsersWithoutGroup ? usersWithoutGroup : filteredUsers).length > 0 ? (
                                                            (showUsersWithoutGroup ? usersWithoutGroup : filteredUsers).map(user => (
                                                                <ListGroup.Item
                                                                    key={user.dni}
                                                                    as="div"
                                                                    onClick={() => toggleUserSelection(user)}
                                                                    active={selectedUsers.some(u => u.dni === user.dni)}
                                                                    className="list-group-item-action d-flex justify-content-between align-items-center p-3"
                                                                    style={{
                                                                        borderBottom: "1px solid #f1f3f4",
                                                                        cursor: "pointer"
                                                                    }}
                                                                >
                                                                    <div>
                                                                        <div className="fw-bold">
                                                                            {user.name} {user.apellidos}
                                                                        </div>
                                                                        <small className="text-muted">
                                                                            {user.cargo || 'Sin cargo'} · DNI: {user.dni}
                                                                        </small>
                                                                        <div>
                                                                            <small>{user.email}</small>
                                                                        </div>
                                                                    </div>
                                                                    <Button
                                                                        variant={selectedUsers.some(u => u.dni === user.dni) ? "danger" : "primary"}
                                                                        size="sm"
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleUserSelection(user);
                                                                        }}
                                                                        style={{
                                                                            borderRadius: "8px",
                                                                            padding: "4px 12px"
                                                                        }}
                                                                    >
                                                                        {selectedUsers.some(u => u.dni === user.dni) ? "Quitar" : "Seleccionar"}
                                                                    </Button>
                                                                </ListGroup.Item>
                                                            ))
                                                        ) : (
                                                            <ListGroup.Item className="text-center py-5">
                                                                <div
                                                                    className="bg-light rounded-circle mx-auto mb-3"
                                                                    style={{
                                                                        width: "60px",
                                                                        height: "60px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                    }}
                                                                >
                                                                    <FaUsers size={24} className="text-muted" />
                                                                </div>
                                                                <h6 className="text-muted mb-2">
                                                                    {showUsersWithoutGroup
                                                                        ? "Todos los usuarios están asignados a grupos"
                                                                        : "No se encontraron usuarios"}
                                                                </h6>
                                                                <p className="text-muted mb-0">
                                                                    {searchTerm || cargoFilter
                                                                        ? "Intenta ajustar los filtros de búsqueda"
                                                                        : "No hay usuarios disponibles"}
                                                                </p>
                                                            </ListGroup.Item>
                                                        )}
                                                    </ListGroup>
                                                </div>
                                            </Card.Body>
                                        </Card>

                                        {/* Lista de grupos existentes */}
                                        <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                            <Card.Header
                                                className="text-white py-3"
                                                style={{
                                                    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                    borderRadius: "15px 15px 0 0",
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0 fw-semibold">Grupos Existentes</h5>
                                                    <Badge bg="light" text="dark">
                                                        {groups.length} grupos
                                                    </Badge>
                                                </div>
                                            </Card.Header>
                                            <Card.Body className="p-0">
                                                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                                    <ListGroup variant="flush">
                                                        {groups.length > 0 ? (
                                                            groups.map(group => (
                                                                <ListGroup.Item
                                                                    key={group.id}
                                                                    as="div"
                                                                    onClick={() => selectGroup(group)}
                                                                    active={currentGroup.id === group.id}
                                                                    className="list-group-item-action d-flex justify-content-between align-items-center p-3"
                                                                    style={{
                                                                        borderBottom: "1px solid #f1f3f4",
                                                                        cursor: "pointer"
                                                                    }}
                                                                >
                                                                    <div>
                                                                        <div className="fw-bold">{group.name}</div>
                                                                        <small className="text-muted">
                                                                            {group.description || 'Sin descripción'} · {(group.members || []).length} miembros
                                                                        </small>
                                                                    </div>
                                                                    <div className="d-flex gap-2">
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                selectGroup(group);
                                                                            }}
                                                                            style={{ borderRadius: "8px" }}
                                                                            title="Editar grupo"
                                                                        >
                                                                            <BiGroup />
                                                                        </Button>
                                                                        <Button
                                                                            variant="outline-danger"
                                                                            size="sm"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleDeleteGroup(group);
                                                                            }}
                                                                            style={{ borderRadius: "8px" }}
                                                                            title="Eliminar grupo"
                                                                        >
                                                                            <FaTrash />
                                                                        </Button>
                                                                    </div>
                                                                </ListGroup.Item>
                                                            ))
                                                        ) : (
                                                            <ListGroup.Item className="text-center py-5">
                                                                <div
                                                                    className="bg-light rounded-circle mx-auto mb-3"
                                                                    style={{
                                                                        width: "60px",
                                                                        height: "60px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                    }}
                                                                >
                                                                    <FaLayerGroup size={24} className="text-muted" />
                                                                </div>
                                                                <h6 className="text-muted mb-2">No hay grupos creados</h6>
                                                                <p className="text-muted mb-3">
                                                                    Crea tu primer grupo para organizar a los usuarios
                                                                </p>
                                                                <Button
                                                                    variant="primary"
                                                                    onClick={createNewGroup}
                                                                    className="d-flex align-items-center gap-2"
                                                                    style={{
                                                                        borderRadius: "8px",
                                                                        background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                                        border: "none"
                                                                    }}
                                                                >
                                                                    <BiUserPlus size="14px" />
                                                                    Empezar
                                                                </Button>
                                                            </ListGroup.Item>
                                                        )}
                                                    </ListGroup>
                                                </div>
                                            </Card.Body>

                                        </Card>
                                    </Col>

                                    {/* Panel derecho: Formulario de grupo y usuarios seleccionados */}
                                    <Col lg={7}>
                                        <Card className="border-0 shadow-sm" style={{ borderRadius: "15px" }}>
                                            <Card.Header
                                                className="text-white py-3"
                                                style={{
                                                    background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                    borderRadius: "15px 15px 0 0",
                                                }}
                                            >
                                                <div className="d-flex justify-content-between align-items-center">
                                                    <h5 className="mb-0 fw-semibold">
                                                        {mode === 'new' ? 'Nuevo Grupo' : 'Editar Grupo'}
                                                    </h5>
                                                    {mode === 'edit' && (
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleDeleteGroup(currentGroup)}
                                                            className="d-flex align-items-center gap-2"
                                                            style={{ borderRadius: "8px" }}
                                                        >
                                                            <FaTrash size="14px" /> Eliminar
                                                        </Button>
                                                    )}
                                                </div>
                                            </Card.Header>
                                            <Card.Body className="p-4">
                                                <Form>
                                                    <Form.Group className="mb-4">
                                                        <Form.Label className="fw-bold">Nombre del grupo</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Ingrese el nombre del grupo"
                                                            value={currentGroup.name}
                                                            onChange={(e) => setCurrentGroup({ ...currentGroup, name: e.target.value })}
                                                            className="form-control-lg"
                                                            style={{
                                                                borderRadius: "10px",
                                                                border: "2px solid #e9ecef"
                                                            }}
                                                        />
                                                    </Form.Group>

                                                    <Form.Group className="mb-4">
                                                        <Form.Label className="fw-bold">Descripción (opcional)</Form.Label>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={3}
                                                            placeholder="Descripción del grupo"
                                                            value={currentGroup.description}
                                                            onChange={(e) => setCurrentGroup({ ...currentGroup, description: e.target.value })}
                                                            style={{
                                                                borderRadius: "10px",
                                                                border: "2px solid #e9ecef"
                                                            }}
                                                        />
                                                    </Form.Group>

                                                    {/* SELECCIÓN DE SEDES (Condicional) */}
                                                    <Form.Group className="mb-4">
                                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                                            <Form.Label className="fw-bold d-flex align-items-center gap-2 mb-0">
                                                                <FaMapMarkerAlt />
                                                                Sedes Asociadas
                                                            </Form.Label>
                                                            <Button
                                                                variant="link"
                                                                size="sm"
                                                                onClick={() => setShowSedesSelector(!showSedesSelector)}
                                                                className="text-decoration-none p-0"
                                                            >
                                                                {showSedesSelector ? 'Ocultar' : (currentGroup.sedes && currentGroup.sedes.length > 0 ? `${currentGroup.sedes.length} seleccionadas` : 'Ver opciones')}
                                                            </Button>
                                                        </div>

                                                        {showSedesSelector && (
                                                            <>
                                                                <div className="border rounded p-3 bg-white" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                                    {sedes.length > 0 ? (
                                                                        sedes.map(sede => (
                                                                            <Form.Check
                                                                                key={sede.id}
                                                                                type="checkbox"
                                                                                id={`sede-${sede.id}`}
                                                                                label={`${sede.nombre} (${sede.regimenNombre || 'Sin régimen'})`}
                                                                                checked={currentGroup.sedes && currentGroup.sedes.includes(sede.id)}
                                                                                onChange={(e) => {
                                                                                    const isChecked = e.target.checked;
                                                                                    let newSedes = currentGroup.sedes || [];
                                                                                    if (isChecked) {
                                                                                        newSedes = [...newSedes, sede.id];
                                                                                    } else {
                                                                                        newSedes = newSedes.filter(id => id !== sede.id);
                                                                                    }
                                                                                    setCurrentGroup({ ...currentGroup, sedes: newSedes });
                                                                                }}
                                                                                className="mb-2"
                                                                            />
                                                                        ))
                                                                    ) : (
                                                                        <p className="text-muted mb-0 small">No hay sedes configuradas para esta empresa.</p>
                                                                    )}
                                                                </div>
                                                                <Form.Text className="text-muted">
                                                                    Las sedes seleccionadas se asignarán automáticamente a todos los miembros del grupo.
                                                                </Form.Text>
                                                            </>
                                                        )}
                                                        {!showSedesSelector && (currentGroup.sedes && currentGroup.sedes.length <= 1) && (
                                                            <div className="text-muted small fst-italic">
                                                                {currentGroup.sedes?.length === 1
                                                                    ? "1 sede asignada automáticamente (click en 'Ver opciones' para editar)."
                                                                    : "Se intentará asignar una sede automáticamente según coincidencia de nombre."}
                                                            </div>
                                                        )}
                                                    </Form.Group>

                                                    <div className="mb-4">
                                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                                            <h6 className="fw-bold mb-0">Usuarios seleccionados</h6>
                                                            <Badge
                                                                bg="primary"
                                                                pill
                                                                style={{ fontSize: "0.9rem", padding: "8px 12px" }}
                                                            >
                                                                {selectedUsers.length} usuarios
                                                            </Badge>
                                                        </div>

                                                        {selectedUsers.length === 0 ? (
                                                            <div className="text-center p-5 border rounded bg-light" style={{ borderRadius: "15px !important" }}>
                                                                <div
                                                                    className="bg-white rounded-circle mx-auto mb-3"
                                                                    style={{
                                                                        width: "60px",
                                                                        height: "60px",
                                                                        display: "flex",
                                                                        alignItems: "center",
                                                                        justifyContent: "center",
                                                                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
                                                                    }}
                                                                >
                                                                    <FaUsers size={24} className="text-muted" />
                                                                </div>
                                                                <h6 className="text-muted mb-2">No hay usuarios seleccionados</h6>
                                                                <p className="text-muted mb-0">
                                                                    Selecciona usuarios de la lista para añadirlos al grupo
                                                                </p>
                                                            </div>
                                                        ) : (
                                                            <div className="selected-users border rounded" style={{ maxHeight: '300px', overflowY: 'auto', borderRadius: "15px" }}>
                                                                <ListGroup variant="flush">
                                                                    {selectedUsers.map(user => (
                                                                        <ListGroup.Item
                                                                            key={user.dni}
                                                                            className="d-flex justify-content-between align-items-center py-3"
                                                                            style={{ borderBottom: "1px solid #f1f3f4" }}
                                                                        >
                                                                            <div>
                                                                                <div className="fw-bold">
                                                                                    {user.name} {user.apellidos}
                                                                                </div>
                                                                                <small className="text-muted">
                                                                                    {user.cargo || 'Sin cargo'} · DNI: {user.dni}
                                                                                </small>
                                                                            </div>
                                                                            <Button
                                                                                variant="outline-danger"
                                                                                size="sm"
                                                                                onClick={() => removeSelectedUser(user)}
                                                                                style={{ borderRadius: "8px" }}
                                                                            >
                                                                                <BiX size="18px" />
                                                                            </Button>
                                                                        </ListGroup.Item>
                                                                    ))}
                                                                </ListGroup>
                                                            </div>
                                                        )}
                                                    </div>
                                                </Form>
                                            </Card.Body>
                                            <Card.Footer className="p-4 bg-light" style={{ borderRadius: "0 0 15px 15px" }}>
                                                <Row className="g-3">
                                                    <Col md={6}>
                                                        <Button
                                                            variant="outline-secondary"
                                                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                            onClick={createNewGroup}
                                                            style={{
                                                                borderRadius: "10px",
                                                                padding: "12px"
                                                            }}
                                                        >
                                                            <BiUserX size="18px" />
                                                            Limpiar
                                                        </Button>
                                                    </Col>
                                                    <Col md={6}>
                                                        <Button
                                                            variant="primary"
                                                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                                                            disabled={loading}
                                                            onClick={saveGroup}
                                                            style={{
                                                                borderRadius: "10px",
                                                                background: "linear-gradient(135deg, #1e3a8a 0%, #3b82f6 100%)",
                                                                border: "none",
                                                                padding: "12px",
                                                                boxShadow: "0 4px 8px rgba(59, 130, 246, 0.3)"
                                                            }}
                                                        >
                                                            <BiSave size="18px" />
                                                            {loading ? 'Guardando...' : (mode === 'new' ? 'GUARDAR GRUPO' : 'GUARDAR CAMBIOS')}
                                                        </Button>
                                                    </Col>
                                                </Row>
                                            </Card.Footer>
                                        </Card>
                                    </Col>
                                </Row>
                            </>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </div>
    );
};

export default GruposEditor;
